import React, { useState, useEffect } from "react";

import functionsService from "../services/functionsService";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";

import { Label } from "./ui/label";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";

import { useToast } from "./hooks/use-toast";

export default function CreateInviteModal({
  open,
  setOpen,
  inviteType,
  projectId,
}) {
  const [email, setEmail] = useState("");
  const [applicationRole, setApplicationRole] = useState(
    inviteType === "project" ? "user" : ""
  );
  const [projectRole, setProjectRole] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const { toast } = useToast();

  // Function to check if the email is valid
  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate fields on input change
  useEffect(() => {
    if (
      isEmailValid(email) &&
      ((inviteType === "application" && applicationRole.length > 0) ||
        (inviteType === "project" && projectRole.length > 0))
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [email, inviteType, applicationRole, projectRole]);

  const clearModal = () => {
    setEmail("");
    setApplicationRole("");
    setProjectRole("");
  };

  const handleCancel = () => {
    clearModal();
    setOpen(false);
  };

  const handleSubmit = async () => {
    try {
      setIsSending(true);

      const { success } = await functionsService.createInvite(
        email,
        inviteType,
        applicationRole,
        projectId,
        projectRole
      );

      if (!success) {
        console.error("Error sending the invite.");

        toast({
          variant: "destructive",
          title: "Error sending the invite",
        });

        return;
      }

      toast({
        title: "Invite sent",
        description: `The invite to "${email}" was successfully sent.`,
      });

      setOpen(false);
      clearModal();
    } catch (error) {
      console.error("Error sending the invite:", error);

      toast({
        variant: "destructive",
        title: "Error sending the invite.",
        description: error.message,
      });
    } finally {
      setIsSending(false);
    }
  };

  const styles = {
    submitButton: {
      minWidth: "115px",
    },

    spinner: {
      border: "4px solid #f3f3f3",
      borderTop: "4px solid #282c34",
      borderRadius: "50%",
      width: "20px",
      height: "20px",
      animation: "spin 1s linear infinite",
    },
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogTitle>Create Invite</DialogTitle>

        <DialogDescription>
          Enter the email and role of the person you want to invite.
        </DialogDescription>

        <div className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>

            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter invitee's email"
              disabled={isSending}
            />
          </div>

          {inviteType === "application" && (
            <div>
              <Label htmlFor="applicationRole">Application Role</Label>

              <Select
                id="applicationRole"
                value={applicationRole}
                onValueChange={setApplicationRole}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select application role" />
                </SelectTrigger>

                <SelectContent className="z-[10001]">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {inviteType === "project" && (
            <div>
              <Label htmlFor="projectRole">Project Role</Label>

              <Select
                id="projectRole"
                value={projectRole}
                onValueChange={setProjectRole}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project role" />
                </SelectTrigger>

                <SelectContent className="z-[10001]">
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" disabled={isSending} onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            disabled={!isValid || isSending}
            onClick={handleSubmit}
            style={styles.submitButton}
          >
            {isSending ? <div style={styles.spinner}></div> : "Create Invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
